import type { ModuleID, VersionString } from "@bestowinc/tinkertoy-core";

import type { LoadedModule, ModuleLoader } from "../module-loader";

// ============================================================================
// Deduped loads (one promise per moduleId + version)
// ============================================================================

function makeKey(moduleId: ModuleID, version: VersionString): string {
  return `${moduleId}:${version}`;
}

const loadPromises = new Map<string, Promise<LoadedModule>>();

/** Same cached promise for preload and subscribers — use from effects (no Suspense). */
export function getModuleLoadPromise(
  loader: ModuleLoader,
  moduleId: ModuleID,
  version: VersionString,
): Promise<LoadedModule> {
  const key = makeKey(moduleId, version);
  let p = loadPromises.get(key);
  if (!p) {
    p = loader.loadModule(moduleId, version);
    const pending = p;
    // Evict failed loads so transient errors (CDN blips, network) don't
    // permanently brick the module for the rest of the session. Identity-guard
    // the delete so a late-firing rejection can't clobber a fresh promise that
    // was inserted under the same key after a cache reset.
    void pending.catch(() => {
      if (loadPromises.get(key) === pending) {
        loadPromises.delete(key);
      }
    });
    loadPromises.set(key, p);
  }
  return p;
}

/**
 * Clears the module load dedupe cache.
 *
 * @remarks Not part of the supported public API for apps — tests (and rare dev tooling)
 * import this to reset module-scope state between cases.
 *
 * @internal
 */
export function resetModuleReadResourceCache(): void {
  loadPromises.clear();
}
